import { useState } from "react";
import PropTypes from "prop-types";
import { RiDeleteBin6Line } from "react-icons/ri";
import defaultImage from "../../../../utils/DefaultImage";
import DashboardTooltip from "../../Components/DashboardTooltip";
import ImagePreviewModal from "../../Components/ImagePreviewModal";

const ExistingWinesOrder = ({
    isVisible,
    selectedWines,
    handleDirectQuantityInput,
    handleRemoveWine,
    cleanImageUrl,
    title = "Vini finali per la proposta",
    canRemove = true
}) => {
    const [selectedImage, setSelectedImage] = useState(null);

    // Helper to get formatted display quantity
    const getDisplayQuantity = (quantity) => {
        if (quantity === undefined || quantity === null) return 1;
        return quantity;
    };

    // Prevent non-numeric characters, decimal points, and leading zero
    const handleQuantityKeyDown = (e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
            e.preventDefault();
        }
        if (e.key === "0" && (!e.target.value || e.target.value === "")) {
            e.preventDefault();
        }
    };

    // Quantity change handler
    const handleQuantityChange = (index, value) => {
        handleDirectQuantityInput(index, value);
    };

    // Quantity blur handler - defaults to 1 if empty or invalid
    const handleQuantityBlur = (index, currentQty) => {
        if (!currentQty || currentQty === "" || Number(currentQty) < 1) {
            handleDirectQuantityInput(index, "1");
        }
    };

    return (
        <div className={`w-full bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow overflow-hidden ${isVisible ? "block" : "hidden"}`}>
            <div className="px-6 pt-4 pb-3">
                <h4 className="text-xl font-bold poppins-medium text-[#303030]">{title}</h4>
            </div>

            {/* Selected Proposal Wines Table */}
            <div className="overflow-x-auto">
                <table className="md:w-full w-[750px] text-left wine_table">
                    <thead>
                        <tr className="border-y-2 border-[#8B2433]/30 bg-[#FAF3F4]">
                            <th className="py-3.5 px-3 first:pl-6 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Sr.no</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Immagine</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Nome Vino</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Quantità</th>
                            {canRemove && <th className="py-3.5 px-3 last:pr-6 text-center poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Azione</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {selectedWines && selectedWines.length > 0 ? (
                            selectedWines.map((item, index) => {
                                const wine = item.wine || {};
                                const qty = getDisplayQuantity(item.quantity);

                                return (
                                    <tr key={wine.id || index} className="border-b border-[#8B2433]/12 hover:bg-[#8B2433]/[0.03] transition-colors">
                                        <td className="py-4 px-3 first:pl-6 text-left poppins-medium text-[#303030]">
                                            {index + 1}
                                        </td>
                                        <td className="pr-2 py-4 px-3">
                                            <div 
                                                onClick={() => setSelectedImage(cleanImageUrl(wine.image) || defaultImage)}
                                                className="thumbnail_image rounded-lg w-[50px] h-[60px] bg-white p-1 flex items-center justify-center border border-[#68172394] cursor-pointer hover:shadow-md transition-shadow"
                                                title="Ingrandisci immagine"
                                            >
                                                <img
                                                    className="w-full h-full object-contain"
                                                    src={cleanImageUrl(wine.image) || defaultImage}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultImage;
                                                    }}
                                                    alt={wine.name || "Wine"}
                                                />
                                            </div>
                                        </td>
                                        <td className="py-4 px-3 text-left poppins-medium text-[#303030] text-sm">
                                            <DashboardTooltip text={wine?.name || "N/A"} maxLength={25} />
                                        </td>
                                        
                                        {/* Direct Quantity Input Pill */}
                                        <td className="py-4 px-3 text-left">
                                            <div className="inline-block border border-[#F2C3CA] hover:border-[#8B2433] rounded-full px-3 py-1 bg-white shadow-sm transition-colors">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={3}
                                                    value={qty !== undefined && qty !== null ? qty : ""}
                                                    onKeyDown={handleQuantityKeyDown}
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    onBlur={() => handleQuantityBlur(index, qty)}
                                                    className="w-12 text-center text-sm font-semibold poppins-medium text-[#DB7685] focus:outline-none"
                                                />
                                            </div>
                                        </td>

                                        {canRemove && (
                                            <td className="py-4 px-3 last:pr-6 text-center">
                                                <button
                                                    onClick={() => handleRemoveWine(index)}
                                                    className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all inline-flex items-center justify-center"
                                                    title="Rimuovi Vino"
                                                >
                                                    <RiDeleteBin6Line className="w-5 h-5 text-[#8B2433]" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={canRemove ? 5 : 4} className="text-center py-6 text-gray-500 poppins-regular text-sm bg-white">
                                    Nessun vino trovato
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Image Preview Modal */}
            <ImagePreviewModal
                imageUrl={selectedImage}
                onClose={() => setSelectedImage(null)}
            />
        </div>
    );
};

ExistingWinesOrder.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    selectedWines: PropTypes.array.isRequired,
    handleDirectQuantityInput: PropTypes.func.isRequired,
    handleRemoveWine: PropTypes.func.isRequired,
    cleanImageUrl: PropTypes.func.isRequired,
    title: PropTypes.string,
    canRemove: PropTypes.bool
};

export default ExistingWinesOrder;
